import { FC, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { sprintf } from 'sprintf-js';

import Banner, { BannerProps } from '../Banner';
import type { JumbotronProps } from 'modules/common/components/Jumbotron';
import RequestFormEntryForm from './RequestFormEntryForm';
import HomeRequestFormEntry from './HomeRequestFormEntry';
import LearningRequestFormEntry from './LearningRequestFormEntry';
import { ReduxProps } from './BannerZipcode.container';

import type { VerticalPageJobType } from 'modules/job-types/types';
import { proCountSelector } from 'modules/marketing-pages/duck';
import { useGoogleOptimize } from 'utils/google-optimize-manager.js';
import styles from './BannerZipcode.module.scss';

const GOOGLE_DISPLAY_FIRST_QUESTION_TEST_ID = 'wxnmt7DHR1epwuviQV7mJQ';
const DURATION = 100;
const GOOGLE_DISPLAY_FIRST_QUESTION_TEST_VARIATION = '1';
const MINIMUM_TO_SHOW_PRO_COUNT = 30;

interface OwnProps {
  bannerProps?: Pick<BannerProps, 'shouldUseOverlayPadding' | 'shouldUseOverlayBackgroundColor'>;
  bgImg: string;
  bgImgObjectFit?: 'cover' | 'contain';
  city?: { key?: string; name: string };
  cityGroupName?: string;
  isJumbotronResponsive?: boolean;
  isRequestFormEntryEnabled?: boolean;
  jobType: VerticalPageJobType;
  jumbotronProps?: Omit<JumbotronProps, 'bgImg'>;
  prefecture?: { key?: string; name: string };
  shouldBannerBeFullHeight?: boolean;
}

type Props = OwnProps & ReduxProps;

const BannerZipcode: FC<Props> = ({
  bgImg,
  city = { name: '' },
  cityGroupName = '',
  bannerProps = {},
  isRequestFormEntryEnabled = true,
  jobType,
  jumbotronProps = {},
  openRequestModal,
  prefecture = { name: '' },
  bgImgObjectFit,
  isJumbotronResponsive,
  shouldBannerBeFullHeight,
  translation,
}) => {
  const { t } = useTranslation();
  const proCount = useSelector(proCountSelector);

  useEffect(() => {
    if (jobType.isConciergeBooking) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      (async () => {
        await openRequestModal({ city, jobTypeId: jobType.id, prefecture });
      })();
    }
  }, [city, jobType.isConciergeBooking, jobType.id, openRequestModal, prefecture]);

  const { landingPageSubtitle = '', requestType = '', seoName } = jobType.translations;
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const jobTypeName = seoName || requestType;

  const title =
    jobType.isDirectBooking || jobType.isConciergeBooking
      ? jobTypeName
      : sprintf(translation.bannerTitle4, {
          jobType: jobTypeName,
          jobTypeTitle2: jobTypeName,
          prosCount:
            proCount >= MINIMUM_TO_SHOW_PRO_COUNT
              ? `${proCount.toLocaleString()}${t(
                  'marketingPages:requestFormEntryForm.banner.proCount.withProCount'
                )}`
              : t('marketingPages:requestFormEntryForm.banner.proCount.withoutProCount'),
        });

  const entryFormLabel = jobType.isDirectBooking
    ? landingPageSubtitle
    : t('marketingPages:requestFormEntryForm.label', {
        interpolation: { escapeValue: false },
        jobType: requestType,
      });

  const hasAtleastOneTitleString = prefecture.name || city.name || cityGroupName || title;
  const displayFirstQuestionTest = useGoogleOptimize(
    GOOGLE_DISPLAY_FIRST_QUESTION_TEST_ID,
    DURATION,
    true
  );
  const isDisplayFirstQuestionTest =
    typeof displayFirstQuestionTest === 'object' &&
    displayFirstQuestionTest !== null &&
    displayFirstQuestionTest.isInitialized &&
    displayFirstQuestionTest.variant === GOOGLE_DISPLAY_FIRST_QUESTION_TEST_VARIATION;
  const shouldShowHomeRequestFormEntry =
    jobType.categories.includes('home') &&
    jobType.forms[0] &&
    jobType.forms[0].name === 'propertyType';

  let componentToDisplay;

  const [{ name, options }] = jobType.forms[0].fields;
  const ageFormOptions = ['10s', '20s', '30s', '40s', '50s', 'over60s'];
  const isFirstQuestionAge = name === 'age' && jobType.categories.includes('learning');
  const isOnlyAgeField = options?.every((option) =>
    ageFormOptions.includes(option?.value?.toString())
  );

  if (shouldShowHomeRequestFormEntry)
    componentToDisplay = (
      <HomeRequestFormEntry
        firstField={jobType.forms[0].fields[0]}
        label={entryFormLabel}
        // eslint-disable-next-line react/jsx-no-bind
        onSubmit={async (propertyType) => {
          await openRequestModal({
            city,
            cityGroupName,
            jobTypeId: jobType.id,
            prefecture,
            propertyType,
          });
        }}
      />
    );
  else if (isFirstQuestionAge && isOnlyAgeField && isDisplayFirstQuestionTest)
    componentToDisplay = (
      <LearningRequestFormEntry
        options={options}
        title={jobType.forms[0].title}
        // eslint-disable-next-line react/jsx-no-bind
        onSubmit={async (age) => {
          await openRequestModal({
            city,
            cityGroupName,
            jobTypeId: jobType.id,
            prefecture,
            age,
          });
        }}
      />
    );
  else
    componentToDisplay = (
      <RequestFormEntryForm
        isZipcodeInputShown={!jobType.isConciergeBooking}
        label={entryFormLabel}
        // eslint-disable-next-line react/jsx-no-bind
        onSubmit={async (address) => {
          await openRequestModal({
            address,
            city,
            cityGroupName,
            jobTypeId: jobType.id,
            prefecture,
          });
        }}
      />
    );
  const EntryForm = componentToDisplay;

  return (
    <>
      <Banner
        bgImg={bgImg}
        alt={jobType.title}
        bgImgObjectFit={bgImgObjectFit}
        fullHeight={shouldBannerBeFullHeight}
        isJumbotronResponsive={isJumbotronResponsive}
        jumbotronProps={jumbotronProps}
        jobTypeId={jobType.id}
        {...bannerProps}
      >
        {hasAtleastOneTitleString && (
          <h1 className={styles.header}>
            {prefecture.name}
            {cityGroupName}
            {city.name}
            {(prefecture.name || city.name || cityGroupName) && <br />}
            {title}
          </h1>
        )}
        {isRequestFormEntryEnabled && EntryForm}
      </Banner>
    </>
  );
};

export type { OwnProps as BannerZipcodeProps };
export default BannerZipcode;
